// "The dream": a person of the road in tall woods at dusk → through the thicket of leaves, a home with its lights on → walk toward it and it fades, twice → the path opens onto soft light → her painting emerges.
export const dream = {
  slug: "the-dream",
  duration: 20.5,
  music: "prelude-op28-4",
  captionTail: "Through the thicket of leaves, a home with its lights on.",
  stages: [
    { id: "road", at: 0, hint: "a person of the road" },
    { id: "glimpse", at: 5, hint: "through the thicket of leaves" },
    { id: "illusion", at: 10, hint: "tap or swipe to walk toward it" },
    { id: "light", at: 11.3, hint: "the light that keeps us walking" },
  ],
  hold: { at: 11, need: 2, auto: 18 },
  touch: { kind: "approach", from: 10, until: 11.3 },
  // painted panorama: tall woods at dusk; through the thicket, a home with its lights on
  layers: [
    { src: "/arcs/dream/woods.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, mist: [[0, 0.25], [12, 0.12], [16, 0.3]], mistCol: "#9fb5b8", show: [-1, 0, 99, 99] },
  ],
  approach: { rate: 0.09, music: "nocturne-op48-1" },
  line: { text: "The dream of home is the light that keeps us walking.", from: 13, to: 27, pos: "low" },
  cues: [[1.5, "birdsong"], [3.6, "birdsong"], [6.2, "clink"], [8.4, "clink"]],
  ambient: { scene: "duskwalk", until: 99, then: [[5, "angan"], [11.5, "garden"]] },
  look: { start: 0.08, lift: [0, 1], end: 0.08, yaw: [[0, -0.6], [3, 0.5], [5.5, 0], [20.5, 0]] },
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "dream", pos: "low" },
  speed: [[0, 1], [20.5, 1]],
  reveal: { style: "vertical", from: "center", duration: 10, focus: [{ rect: [0.3, 0.3, 0.4, 0.35], style: "radial" }] },
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform float uT, uPhase, uAspect, uTileH, uRadius, uCy, uSurface, uWarm, uPulse, uStill;
varying vec3 vPos;
${"__NOISE__"}
float hash1(float n){ return fract(sin(n * 91.7) * 43758.5453); }
float hash2(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
// a leaf dab: a short pointed brush mark
float leaf(vec2 p, float s){
  vec2 c = p / s;
  vec2 f = fract(c) - 0.5;
  float h = hash2(floor(c));
  float a = h * 6.28;
  f = mat2(cos(a), -sin(a), sin(a), cos(a)) * f;
  return (1.0 - smoothstep(0.85, 1.0, length(f / vec2(0.42, 0.18)))) * step(0.35, h);
}
// tall trunks at dusk; seed changes the woods, part pushes them aside
vec3 woods(vec2 P, float seed, float part){
  vec3 c = mix(mix(vec3(0.98, 0.72, 0.55), vec3(0.95, 0.62, 0.66), smoothstep(0.8, 2.2, P.y)), vec3(0.32, 0.28, 0.48), smoothstep(2.0, 6.0, P.y));
  c = mix(c, vec3(1.0, 0.92, 0.7), part * exp(-P.x * P.x * 0.6) * (1.0 - smoothstep(0.5, 3.0, P.y)) * 0.8);
  for (int l = 0; l < 2; l++){
    float fl = float(l);
    float w = fl < 0.5 ? 0.55 : 1.1;
    float x = P.x + sign(P.x) * part * (1.5 + fl * 1.5);
    float cell = floor(x / w);
    float h = hash1(cell * 3.1 + seed * 17.0 + fl * 5.0);
    float cx = (cell + 0.25 + 0.5 * h) * w;
    float tw = (fl < 0.5 ? 0.06 : 0.13) + 0.05 * hash1(cell + seed);
    float trunk = (1.0 - smoothstep(tw * 0.8, tw, abs(x - cx + 0.03 * sin(P.y * 2.0 + h * 6.0)))) * step(0.3, h);
    vec3 bark = (fl < 0.5 ? vec3(0.36, 0.26, 0.34) : vec3(0.16, 0.1, 0.12)) * (0.8 + 0.3 * step(0.5, fract(P.y * 6.0 + h * 3.0)));
    c = mix(c, bark, trunk);
  }
  vec3 green = mix(vec3(0.18, 0.32, 0.16), vec3(0.45, 0.62, 0.28), hash2(floor(P / 0.12)));
  c = mix(c, green, leaf(P + seed * 3.0, 0.12) * smoothstep(3.2, 4.2, P.y) * (1.0 - part * 0.6));
  return c;
}
void main(){
  float n = snoise(vPos * 0.6) * 0.5 + 0.5;
  float th = atan(vPos.x, -vPos.z);
  float glimpse = smoothstep(5.0, 7.0, uT);
  float open = smoothstep(11.4, 15.5, uT);
  float fades = uPulse;
  float appr = uWarm;
  float gone = smoothstep(0.8, 1.0, appr);
  vec3 col;
  vec2 local;
  if (uSurface < 0.5) {
    float X = th * uRadius;
    vec2 P = vec2(X, vPos.y);
    col = mix(woods(P, fades, open), woods(P, fades + 1.0, open), gone * (1.0 - step(1.5, fades)));
    // the glimpse: a home with its lights on, glowing through the thicket of leaves
    float s = mix(0.45, 1.5, appr * appr) * mix(1.0, 0.35, open);
    vec2 hq = (P - vec2(0.0, 1.25 + 0.15 * appr)) / s;
    float vis = glimpse * (1.0 - gone) * (1.0 - step(1.5, fades) * (1.0 - open));
    float body = step(abs(hq.x), 0.42) * step(abs(hq.y), 0.25);
    float roof = step(abs(hq.x), 0.5 - (hq.y - 0.25) * 1.4) * step(0.25, hq.y) * step(hq.y, 0.6);
    float win = step(abs(abs(hq.x) - 0.2), 0.08) * step(abs(hq.y - 0.03), 0.08);
    float door = step(abs(hq.x), 0.06) * step(abs(hq.y + 0.12), 0.13);
    vec3 home = mix(vec3(0.42, 0.3, 0.3), vec3(0.3, 0.17, 0.16), roof);
    home = mix(home, vec3(1.0, 0.82, 0.42), max(win, door * 0.8));
    float ring = length(hq * vec2(1.0, 1.2));
    float thicket = leaf(P * 1.0 + 7.0, 0.1) * smoothstep(0.75 + 0.5 * appr, 0.95 + 0.5 * appr, ring) * (1.0 - smoothstep(1.6, 2.4, ring)) * vis;
    col += vec3(1.0, 0.7, 0.35) * exp(-ring * 1.6) * vis * (0.45 + 0.1 * sin(uPhase * 1.3));
    col = mix(col, home, max(body, roof) * vis);
    col = mix(col, mix(vec3(0.15, 0.3, 0.14), vec3(0.32, 0.5, 0.2), hash2(floor(P / 0.1))), thicket);
    // warm wisps of dinner drifting out toward you
    for (int i = 0; i < 3; i++){
      float fi = float(i);
      vec2 w = hq - vec2(-0.2 + fi * 0.2, 0.6);
      float x = 0.12 * w.y * sin(w.y * 3.0 - uPhase * 1.2 + fi * 2.0);
      float wisp = (1.0 - smoothstep(0.01, 0.03 + 0.03 * w.y, abs(w.x - x))) * step(0.0, w.y) * (1.0 - smoothstep(0.6, 1.8, w.y));
      col = mix(col, vec3(1.0, 0.82, 0.6), wisp * vis * 0.4);
    }
    local = vec2(X, P.y - uCy);
  } else if (uSurface < 1.5) {
    vec2 p = vPos.xz;
    col = mix(vec3(0.24, 0.17, 0.14), vec3(0.32, 0.22, 0.16), n);
    vec3 lc = mix(vec3(0.75, 0.42, 0.18), vec3(0.6, 0.55, 0.2), hash2(floor(p / 0.14)));
    col = mix(col, lc, leaf(p, 0.14) * 0.8);
    float path = exp(-p.x * p.x * 1.2) * smoothstep(1.0, -uRadius, p.y);
    col = mix(col, vec3(1.0, 0.85, 0.68), path * open * 0.55);
    local = vPos.xz;
  } else {
    vec2 p = vPos.xz;
    col = mix(vec3(0.38, 0.3, 0.45), mix(vec3(0.12, 0.2, 0.1), vec3(0.3, 0.45, 0.2), hash2(floor(p / 0.15))), leaf(p, 0.15) * (1.0 - open * 0.5));
    local = vPos.xz;
  }
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
};
